import React, { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { CaptionOverlay, CaptionStyles, Caption } from "../../../types";
import { DEFAULT_CAPTION_STYLE } from "../../../templates/caption-templates";

import { AlignLeft, Layers, PaintBucket } from "lucide-react";

import { CaptionStylePanel } from "./caption-style-panel";
import { CaptionTimeline } from "./caption-timeline";
import { CaptionPresetSelector } from "./caption-preset-selector";
import { UnifiedTabs } from "../shared/unified-tabs";
import { useCaptionPresets } from "../../../hooks/use-caption-presets";
import { Id } from "../../../../../../convex/_generated/dataModel";

/**
 * Props for the CaptionSettings component
 * @interface CaptionSettingsProps
 * @property {CaptionOverlay} localOverlay - Current caption overlay being edited
 * @property {Function} setLocalOverlay - Function to update the caption overlay
 * @property {number} currentFrame - Current frame position in the video
 * @property {number} startFrame - Starting frame of the caption overlay
 * @property {Caption[]} captions - Array of caption objects
 */
interface CaptionSettingsProps {
  localOverlay: CaptionOverlay;
  setLocalOverlay: (overlay: CaptionOverlay) => void;
  currentFrame: number;
  startFrame: number;
  captions: Caption[];
}

/**
 * Default styling configuration for captions
 * Uses the DEFAULT_CAPTION_STYLE from caption-templates.ts
 */
export const defaultCaptionStyles: CaptionStyles = DEFAULT_CAPTION_STYLE;

/**
 * CaptionSettings Component
 *
 * @component
 * @description
 * Provides a tabbed interface for managing caption settings including:
 * - Caption text and timing management
 * - Visual style customization
 * - Voice settings (planned feature)
 *
 * The component uses a tab-based layout to organize different aspects of caption
 * configuration, making it easier for users to focus on specific settings.
 *
 * @example
 * ```tsx
 * <CaptionSettings
 *   localOverlay={captionOverlay}
 *   setLocalOverlay={handleOverlayUpdate}
 *   currentFrame={30}
 *   startFrame={0}
 *   captions={[...]}
 * />
 * ```
 */
export const CaptionSettings: React.FC<CaptionSettingsProps> = ({
  localOverlay,
  setLocalOverlay,
  currentFrame,
}) => {
  const currentMs = (currentFrame / 30) * 1000;

  const [searchParams] = useSearchParams();
  const projectIdFromUrl = searchParams.get("projectId");

  // Convert URL param to Convex Id type if available
  const projectId = projectIdFromUrl
    ? (projectIdFromUrl as Id<"projects">)
    : null;

  const { userPresets, savePreset, deletePreset } = useCaptionPresets(projectId);

  // Ensure styles exist with defaults
  const styles = localOverlay.styles || {
    fontFamily: "Inter, sans-serif",
    fontSize: "2.2rem",
    lineHeight: 1.4,
    textAlign: "center" as const,
    color: "#000000",
  };

  /**
   * Apply a preset's styles to the current overlay
   */
  const handleApplyPreset = useCallback(
    (presetStyles: CaptionStyles) => {
      setLocalOverlay({
        ...localOverlay,
        styles: presetStyles,
      });
    },
    [localOverlay, setLocalOverlay]
  );

  return (
    <UnifiedTabs
      defaultValue="captions"
      tabs={[
        {
          value: "captions",
          label: "Edit",
          icon: <AlignLeft className="w-3 h-3" />,
          content: (
            <CaptionTimeline
              localOverlay={localOverlay}
              setLocalOverlay={setLocalOverlay}
              currentMs={currentMs}
            />
          ),
        },
        {
          value: "presets",
          label: "Presets",
          icon: <Layers className="w-3 h-3" />,
          content: (
            <CaptionPresetSelector
              currentStyles={styles}
              onApplyPreset={handleApplyPreset}
              userPresets={userPresets}
              onSavePreset={savePreset}
              onDeletePreset={deletePreset}
            />
          ),
        },
        {
          value: "display",
          label: "Style",
          icon: <PaintBucket className="w-3 h-3" />,
          content: (
            <CaptionStylePanel
              localOverlay={localOverlay}
              setLocalOverlay={setLocalOverlay}
            />
          ),
        },
      ]}
    />
  );
};
